import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CSATReports from '../../api/csatReports';
import { downloadCsvFile } from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';

const computeDistribution = (value, total) =>
  ((value * 100) / total).toFixed(2);

export const state = {
  records: [],
  metrics: {
    totalResponseCount: 0,
    ratingsCount: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    },
    totalSentMessagesCount: 0,
    fistContactResolution: 0,
    rankingCsat: 0
  },
  uiFlags: {
    isFetching: false,
    isFetchingMetrics: false,
    isRefreshing: false,
  },
  metricsCachedAt: null,
  cooldownRemaining: 0,
};

export const getters = {
  getCSATResponses(_state) {
    return _state.records;
  },
  getMetrics(_state) {
    return _state.metrics;
  },
  getMetricsCachedAt(_state) {
    return _state.metricsCachedAt;
  },
  getCooldownRemaining(_state) {
    return _state.cooldownRemaining;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getSatisfactionScore(_state) {
    if (!_state.metrics.totalResponseCount) {
      return 0;
    }
    return computeDistribution(
      _state.metrics.ratingsCount[4] + _state.metrics.ratingsCount[5],
      _state.metrics.totalResponseCount
    );
  },
  getResponseRate(_state) {
    if (!_state.metrics.totalSentMessagesCount) {
      return 0;
    }
    return computeDistribution(
      _state.metrics.totalResponseCount,
      _state.metrics.totalSentMessagesCount
    );
  },
  getRatingPercentage(_state) {
    if (!_state.metrics.totalResponseCount) {
      return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    }
    return {
      1: computeDistribution(
        _state.metrics.ratingsCount[1],
        _state.metrics.totalResponseCount
      ),
      2: computeDistribution(
        _state.metrics.ratingsCount[2],
        _state.metrics.totalResponseCount
      ),
      3: computeDistribution(
        _state.metrics.ratingsCount[3],
        _state.metrics.totalResponseCount
      ),
      4: computeDistribution(
        _state.metrics.ratingsCount[4],
        _state.metrics.totalResponseCount
      ),
      5: computeDistribution(
        _state.metrics.ratingsCount[5],
        _state.metrics.totalResponseCount
      ),
    };
  },
};

export const actions = {
  get: async function getResponses({ commit }, params) {
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetching: true });
    try {
      const response = await CSATReports.get(params);
      commit(types.SET_CSAT_RESPONSE, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetching: false });
    }
  },
  getMetrics: async function getMetrics({ commit, dispatch }, params) {
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingMetrics: true });
    try {
      const response = await CSATReports.getMetrics(params);
      commit(types.SET_CSAT_RESPONSE_METRICS, response.data);
      if (response.data.cached_at) {
        commit(types.SET_CSAT_METRICS_CACHED_AT, response.data.cached_at);
      }
      // Backend had no cache and triggered a job — poll until ready
      if (response.data.refreshing) {
        commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isRefreshing: true });
        const poll = setInterval(async () => {
          try {
            const statusResp = await CSATReports.getRefreshStatus(params);
            if (!statusResp.data.refreshing) {
              clearInterval(poll);
              commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isRefreshing: false });
              dispatch('getMetrics', params);
            }
          } catch {
            clearInterval(poll);
            commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isRefreshing: false });
          }
        }, 3000);
      }
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingMetrics: false });
    }
  },
  refreshMetrics: async function refreshMetrics({ commit, dispatch }, params) {
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isRefreshing: true });
    try {
      const resp = await CSATReports.refreshMetrics(params);
      if (resp.data.cooldown_remaining) {
        commit(types.SET_CSAT_COOLDOWN, resp.data.cooldown_remaining);
      }
      if (resp.status === 429) {
        commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isRefreshing: false });
        return;
      }
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const statusResp = await CSATReports.getRefreshStatus(params);
          if (statusResp.data.cooldown_remaining) {
            commit(types.SET_CSAT_COOLDOWN, statusResp.data.cooldown_remaining);
          }
          if (!statusResp.data.refreshing) {
            clearInterval(poll);
            commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isRefreshing: false });
            dispatch('getMetrics', params);
          }
        } catch {
          clearInterval(poll);
          commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isRefreshing: false });
        }
      }, 3000);
    } catch (error) {
      // 429 comes as error in axios
      if (error?.response?.data?.cooldown_remaining) {
        commit(types.SET_CSAT_COOLDOWN, error.response.data.cooldown_remaining);
      }
      commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isRefreshing: false });
    }
  },
  downloadCSATReports(_, params) {
    return CSATReports.download(params).then(response => {
      downloadCsvFile(params.fileName, response.data);
      AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
        reportType: 'csat',
      });
    });
  },
  checkCooldown: async function checkCooldown({ commit }, params) {
    try {
      const resp = await CSATReports.getRefreshStatus(params);
      if (resp.data.cooldown_remaining > 0) {
        commit(types.SET_CSAT_COOLDOWN, resp.data.cooldown_remaining);
      }
    } catch {
      // ignore
    }
  },
};

export const mutations = {
  [types.SET_CSAT_RESPONSE_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_CSAT_RESPONSE]: MutationHelpers.set,
  [types.SET_CSAT_RESPONSE_METRICS](
    _state,
    {
      total_count: totalResponseCount,
      ratings_count: ratingsCount,
      total_sent_messages_count: totalSentMessagesCount,
      fist_contact_resolution: fistContactResolution,
      ranking_csat: rankingCsat
    }
  ) {
    _state.metrics.totalResponseCount = totalResponseCount || 0;
    _state.metrics.fistContactResolution = fistContactResolution || 0;
    _state.metrics.ratingsCount = {
      1: ratingsCount['1'] || 0,
      2: ratingsCount['2'] || 0,
      3: ratingsCount['3'] || 0,
      4: ratingsCount['4'] || 0,
      5: ratingsCount['5'] || 0,
    };
    _state.metrics.totalSentMessagesCount = totalSentMessagesCount || 0;
    _state.metrics.rankingCsat = rankingCsat || 0;
  },
  [types.SET_CSAT_METRICS_CACHED_AT](_state, cachedAt) {
    _state.metricsCachedAt = cachedAt;
  },
  [types.SET_CSAT_COOLDOWN](_state, seconds) {
    _state.cooldownRemaining = seconds;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};